import React, { useEffect, useState } from 'react';
import { motion } from 'framer-motion';
import { TrendingDown, LineChart, Activity } from 'lucide-react';
import { InteriorSlide } from './SlideShell';
import type { SlideProps } from './SlideShell';
const LEVERS = [{
  title: 'Reduce erosion',
  desc: 'Continuous, data-driven engagement keeps HCPs connected to the brand — actively defending share instead of letting it fade.',
  icon: TrendingDown,
  surface: 'border-[var(--brand)]/25 bg-[var(--brand)]/[0.05]',
  chip: 'bg-[var(--brand)]/12 text-[var(--brand)]'
}, {
  title: 'Increase profitability',
  desc: 'Replacing traditional marketing with automated execution widens the margin on every euro of established brand revenue.',
  icon: LineChart,
  surface: 'border-[var(--accent)]/30 bg-[var(--accent)]/[0.06]',
  chip: 'bg-[var(--accent)]/15 text-[var(--accent)]'
}, {
  title: 'Prolong lifespan',
  desc: 'Lower costs and slower erosion keep the gap between revenue and cost positive for longer, keeping products on market.',
  icon: Activity,
  surface: 'border-black/10 bg-black/[0.02]',
  chip: 'bg-black/5 text-[var(--fg)]'
}] as const;
const MAIN_CURVE = 'M 80 290 C 140 290 170 380 230 380 C 290 380 340 340 380 290 C 410 250 430 130 460 90 C 540 70 650 70 730 90 C 745 95 760 245 785 275 C 850 283 920 287 960 290';
const POTENTIAL_CURVE = 'M 730 90 C 775 100 790 245 815 268 C 875 265 935 267 985 268';
const POTENTIAL_AREA = 'M 730 90 C 775 100 790 245 815 268 C 875 265 935 267 985 268 L 960 290 C 920 287 850 283 785 275 C 760 245 745 95 730 90 Z';
const PHASES = [{
  label: 'R&D',
  sub: 'Investment & clinical trials',
  x: 230
}, {
  label: 'Launch',
  sub: 'Heavy marketing spend',
  x: 420
}, {
  label: 'Mature',
  sub: 'Peak revenue & share',
  x: 595
}, {
  label: 'Established',
  sub: 'Loss of exclusivity',
  x: 845,
  accent: true
}];
const AXIS = '#D8DEE7';
export function SlideLifecycleTail({}: SlideProps) {
  const [stage, setStage] = useState(0);
  useEffect(() => {
    const timers = [setTimeout(() => setStage(1), 600), setTimeout(() => setStage(2), 1400), setTimeout(() => setStage(3), 4000), setTimeout(() => setStage(4), 5800), setTimeout(() => setStage(5), 6600)];
    return () => timers.forEach(clearTimeout);
  }, []);
  return <InteriorSlide data-id="element-437" data-mp="3at1gzjxw5q5j">
      <motion.div initial={{
      opacity: 0,
      y: 20
    }} animate={{
      opacity: 1,
      y: 0
    }} transition={{
      duration: 0.6
    }} className="text-center flex-shrink-0" data-id="element-438" data-mp="3ashx3xookrov">
        <p className="text-[var(--brand)] text-[clamp(0.7rem,1vw,0.9rem)] font-semibold uppercase tracking-[0.18em] mb-2" data-id="element-439" data-mp="2cy85av1lvfm">
          Established Portfolio
        </p>
        <h2 className="text-[var(--fg)] font-bold text-[clamp(1.75rem,3.2vw,2.9rem)] leading-tight tracking-tight" data-id="element-440" data-mp="cbxhywrjnx47">
          Unlocking the potential of the tail
        </h2>
      </motion.div>

      <div className="flex-1 min-h-0 relative flex items-center justify-center py-2" data-id="element-441" data-mp="qyw4cdbjo1c2">
        <div className="w-full max-w-5xl max-h-full relative" style={{
        aspectRatio: '2.2 / 1'
      }} data-id="element-442" data-mp="qx1nba3e8td9">
          <svg viewBox="0 0 1000 480" className="w-full h-full overflow-visible" data-id="element-443" data-mp="gzn7ncpao8m0">
            <defs data-id="element-444" data-mp="lz03gmy58q0q">
              <pattern id="lifecycle-hatch" patternUnits="userSpaceOnUse" width="8" height="8" patternTransform="rotate(45)" data-id="element-445" data-mp="lyhx2xhrovf3">
                <line x1="0" y1="0" x2="0" y2="8" stroke="#F36633" strokeWidth="1.5" strokeOpacity="0.45" data-id="element-446" data-mp="c2wqem1r76z0" />
              </pattern>
            </defs>

            {/* Axis annotations */}
            <motion.g initial={{
            opacity: 0
          }} animate={{
            opacity: stage >= 1 ? 1 : 0
          }} transition={{
            duration: 0.5
          }} data-id="element-447" data-mp="3pgi7ejaspcvv">
              <text x="60" y="100" textAnchor="middle" className="text-xs font-medium" fill="#9CA3AF" data-id="element-448" data-mp="rnq8l7s08ri0">
                +
              </text>
              <text x="60" y="385" textAnchor="middle" className="text-xs font-medium" fill="#9CA3AF" data-id="element-449" data-mp="cxsjmpm93xrr">
                −
              </text>
              <text x="30" y="240" textAnchor="middle" className="text-xs font-medium" fill="#6B7280" style={{
              writingMode: 'vertical-rl',
              textOrientation: 'mixed'
            }} data-id="element-450" data-mp="3y5ebsqei7tf">
                Cumulative Value
              </text>
              <text x="520" y="455" textAnchor="middle" className="text-xs font-medium" fill="#6B7280" data-id="element-451" data-mp="20urx3plvmkqg">
                Time
              </text>
              <text x="975" y="295" className="text-[10px] font-medium" fill="#9CA3AF" data-id="element-452" data-mp="2anpji6tzotuc">
                €0
              </text>
            </motion.g>

            {/* Y axis */}
            <motion.line x1="80" y1="40" x2="80" y2="430" stroke={AXIS} strokeWidth="2" initial={{
            pathLength: 0
          }} animate={{
            pathLength: stage >= 1 ? 1 : 0
          }} transition={{
            duration: 0.6
          }} data-id="element-453" data-mp="1hyiauf0tmnvd" />

            {/* Zero line */}
            <motion.line x1="80" y1="290" x2="970" y2="290" stroke={AXIS} strokeWidth="2" initial={{
            pathLength: 0
          }} animate={{
            pathLength: stage >= 1 ? 1 : 0
          }} transition={{
            duration: 0.8
          }} data-id="element-454" data-mp="3894ugbg9ayj2" />

            {/* Phase dividers */}
            <motion.g initial={{
            opacity: 0
          }} animate={{
            opacity: stage >= 3 ? 1 : 0
          }} transition={{
            duration: 0.5
          }} data-id="element-455" data-mp="1d43zwgn34lxo">
              {[380, 460, 730].map(x => <line key={x} x1={x} y1="50" x2={x} y2="430" stroke={AXIS} strokeWidth="1" strokeDasharray="4,4" data-id="element-456" data-mp="yasd93jm476b" />)}
            </motion.g>

            {/* Legacy phase highlight */}
            <motion.rect x="730" y="50" width="230" height="380" fill="#F36633" fillOpacity="0.05" initial={{
            opacity: 0
          }} animate={{
            opacity: stage >= 4 ? 1 : 0
          }} transition={{
            duration: 0.6
          }} data-id="element-457" data-mp="3nrquqbctc6v6" />

            {/* Potential area */}
            <motion.path d={POTENTIAL_AREA} fill="url(#lifecycle-hatch)" initial={{
            opacity: 0
          }} animate={{
            opacity: stage >= 4 ? 1 : 0
          }} transition={{
            duration: 1
          }} data-id="element-458" data-mp="fd8q6jm2z9gb" />

            {/* Main curve */}
            <motion.path d={MAIN_CURVE} fill="none" stroke="#1A1A1A" strokeWidth="3" initial={{
            pathLength: 0
          }} animate={{
            pathLength: stage >= 2 ? 1 : 0
          }} transition={{
            duration: 2.6,
            ease: 'easeInOut'
          }} data-id="element-459" data-mp="2q4qpmi858l94" />

            {/* Potential dashed extension */}
            <motion.path d={POTENTIAL_CURVE} fill="none" stroke="#F36633" strokeWidth="3" strokeDasharray="8,4" initial={{
            pathLength: 0
          }} animate={{
            pathLength: stage >= 4 ? 1 : 0
          }} transition={{
            duration: 1.4,
            ease: 'easeInOut'
          }} data-id="element-460" data-mp="1tgj6xwcl9oqj" />

            {/* Phase labels */}
            <motion.g initial={{
            opacity: 0
          }} animate={{
            opacity: stage >= 3 ? 1 : 0
          }} transition={{
            duration: 0.5
          }} data-id="element-461" data-mp="38xba5bbsylt3">
              {PHASES.map(p => <g key={p.label} data-id="element-462" data-mp="vmrxq6k39fim">
                  <text x={p.x} y="450" textAnchor="middle" className="text-sm font-semibold" fill={p.accent ? '#F36633' : '#374151'} data-id="element-463" data-mp="zm95x7qwgqrh">
                    {p.label}
                  </text>
                  <text x={p.x} y="468" textAnchor="middle" className="text-xs" fill={p.accent ? '#F58A5E' : '#9CA3AF'} data-id="element-464" data-mp="1eirhwv6trern">
                    {p.sub}
                  </text>
                </g>)}
              <text x="230" y="410" textAnchor="middle" className="text-[10px] italic" fill="#9CA3AF" data-id="element-465" data-mp="1scr3xucro1y1">
                Investment
              </text>
              <text x="595" y="55" textAnchor="middle" className="text-[10px] italic" fill="#9CA3AF" data-id="element-466" data-mp="110cd2gcv8qaw">
                Peak Revenue
              </text>
            </motion.g>

            {/* Potential callout */}
            <motion.g initial={{
            opacity: 0
          }} animate={{
            opacity: stage >= 4 ? 1 : 0
          }} transition={{
            duration: 0.6,
            delay: 0.6
          }} data-id="element-467" data-mp="1esil1f5v7qz9">
              <line x1="845" y1="215" x2="900" y2="178" stroke="#F36633" strokeWidth="1" strokeOpacity="0.6" data-id="element-468" data-mp="1ssa2xqzi5rij" />
              <text x="908" y="170" className="text-sm font-bold tracking-wider" fill="#F36633" data-id="element-469" data-mp="3ujn72dtnbvdj">
                POTENTIAL
              </text>
              <text x="908" y="186" className="text-[10px]" fill="#F58A5E" data-id="element-470" data-mp="3i6sxclw2qqm4">
                Softening the cliff
              </text>
            </motion.g>
          </svg>
        </div>
      </div>

      <motion.div initial={{
      opacity: 0,
      y: 20
    }} animate={{
      opacity: stage >= 5 ? 1 : 0,
      y: stage >= 5 ? 0 : 20
    }} transition={{
      duration: 0.5
    }} className="grid grid-cols-1 md:grid-cols-3 gap-4 max-w-5xl mx-auto w-full flex-shrink-0 mt-4" data-id="element-471" data-mp="3964ijoyuwm86">
        {LEVERS.map(lever => {
        const Icon = lever.icon;
        return <div key={lever.title} className={`rounded-2xl border p-4 ${lever.surface}`} data-id="element-472" data-mp="2liwok36vozlg">
              <div className="flex items-center gap-2.5 mb-2" data-id="element-473" data-mp="2ghyy73bpsz8v">
                <div className={`h-8 w-8 rounded-lg flex items-center justify-center flex-shrink-0 ${lever.chip}`} data-id="element-474" data-mp="2i0fpl6izdbu9">
                  <Icon className="h-4 w-4" data-id="element-475" data-mp="2mxpmf78p8ps4" />
                </div>
                <h3 className="text-[var(--fg)] font-bold text-sm" data-id="element-476" data-mp="28z956md7ngvq">
                  {lever.title}
                </h3>
              </div>
              <p className="text-[var(--muted)] text-[13px] leading-snug" data-id="element-477" data-mp="2d3gw8q7558x4">
                {lever.desc}
              </p>
            </div>;
      })}
      </motion.div>
    </InteriorSlide>;
}